import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatToolbar } from '@angular/material/toolbar';
import { RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { TooltipModule } from 'primeng/tooltip';
import { take } from 'rxjs';

import { getHideSuperuserMessagesFlag, isChromeBrowser } from '@@core/helpers/core.helper';
import { UserRolePermission } from '@@core/models/auth/auth-model';
import { BrowserExtensionService } from '@@core/services/browser-extension.service';
import { ConfirmationDialogService } from '@@core/services/confirmation-dialog.service';
import { LDFeatureKey } from '@@core/services/launch-darkly.service';
import { UserPreferencesService } from '@@core/services/user-preferences.service';
import { PermissionsService } from '@@settings/services/permissions.service';
import { AvatarComponent } from '@@shared/avatar';
import { IconComponent } from '@@shared/design/components/icon/icon.component';
import { IconSize } from '@@shared/design/components/icon/icon.model';
import { TagComponent } from '@@shared/design/components/tag/tag.component';
import { TagSeverity, TagSize } from '@@shared/design/components/tag/tag.model';
import { ConfirmationDialogData } from '@@shared/dialog';
import { Docs } from '@@shared/docs/docs.const';
import { JoinPipe } from '@@shared/pipes/join-pipe';
import { APP_CONFIG } from '@@shared/providers/application-config-provider/application-config-provider.model';
import * as GlobalStoreActions from '@@shared/stores/app-stores/global-store/actions/global-store.actions';
import { selectLoginAsUserId } from '@@shared/stores/app-stores/global-store/selectors/global-store.selectors';
import { AuthStore } from '@@shared/stores/auth-store/stores/auth.store';
import { ConfigurationStore } from '@@shared/stores/configuration-store/stores/configuration.store';
import { ManualTestLauncherComponent } from '@@widgets/manual-test/components/manual-test-launcher/manual-test-launcher.component';
import { ManualTestService } from '@@widgets/manual-test/services/manual-test.service';

import { FeatureService } from '../../core/services/features.service';
import { AuthService } from '../../features/auth/services/auth.service';
import { ChangePasswordDialogComponent } from './change-password-dialog/change-password-dialog.component';
import { HeaderButtonComponent } from './header-button/header-button/header-button.component';
import { HeaderAnalyticsService } from './services/header-analytics.service';

@Component({
	selector: 'sl-header',
	templateUrl: './header.component.html',
	styleUrl: './header.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		AsyncPipe,
		MatToolbar,
		TagComponent,
		RouterModule,
		TooltipModule,
		AvatarComponent,
		MatDivider,
		JoinPipe,
		MatMenu,
		MatMenuTrigger,
		MatMenuItem,
		ManualTestLauncherComponent,
		HeaderButtonComponent,
		IconComponent,
	],
	providers: [HeaderAnalyticsService],
})
export class HeaderComponent {
	hideSuperuserMessages = getHideSuperuserMessagesFlag();
	readonly DocsLinks = Docs;

	readonly customTabsSignal$ = inject(ConfigurationStore).customTabs;
	readonly impersonatedUser$ = inject(Store).select(selectLoginAsUserId);
	readonly isUserSealightsAdminSignal$ = inject(AuthStore).isSealightsAdminUser;
	readonly isDevOpsRoleSignal$ = inject(AuthStore).isDevopsUser;
	readonly isUserSealightsAdmin$ = toObservable(this.isUserSealightsAdminSignal$);
	readonly isDevOpsRole$ = toObservable(this.isDevOpsRoleSignal$);
	readonly isFlagsPageActiveSignal$ = toSignal(inject(FeatureService).isFeatureOn(LDFeatureKey.FF_PAGE));
	readonly isMultiAppsCoverageActiveSignal$ = toSignal(
		inject(FeatureService).isFeatureOn(LDFeatureKey.MULTI_APP_COVERAGE),
	);
	readonly isTgaActiveSignal$ = toSignal(inject(FeatureService).isFeatureOn(LDFeatureKey.TGA));
	readonly isSavingSummaryActiveSignal$ = toSignal(
		inject(FeatureService).isFeatureOn(LDFeatureKey.DYNAMIC_TEST_STAGES_FOR_TIA),
	);
	readonly isCoverageReportsTgaActive$ = toSignal(
		inject(FeatureService).isFeatureOn(LDFeatureKey.COVERAGE_REPORTS_TGA),
	);
	readonly isCiMonitorFeatureActiveSignal$ = toSignal(inject(FeatureService).isFeatureOn(LDFeatureKey.CI_MONITOR));
	readonly isLiveLabsEnabledSignal$ = toSignal(inject(FeatureService).isFeatureOn(LDFeatureKey.LIVE_LABS_MONITOR));
	readonly isTestManagementTabVisibleSignal$ = toSignal(
		inject(FeatureService).isFeatureOn(LDFeatureKey.TEST_MANAGEMENT_TAB_VISIBLE),
	);
	readonly userNameSignal$ = inject(AuthStore).fullName;
	readonly userSignal$ = inject(AuthStore).user;
	readonly user$ = toObservable(this.userSignal$);
	readonly avatarUserDataSignal$ = inject(AuthStore).avatarInfo;
	readonly isCockpitAllowedSignal$ = computed(() => UserRolePermission.COCKPIT.includes(this.userSignal$()?.role));
	readonly permissionsState$ = inject(PermissionsService).stateObservable$;
	readonly isManualTestRunnerFeatureActiveSignal$ = toSignal(
		inject(FeatureService).isFeatureOn(LDFeatureKey.MANUAL_TEST_RUNNER),
	);
	// readonly isTroubleshootingFeatureActiveSignal$ = toSignal(inject(FeatureService).isFeatureOn(LDFeatureKey.TROUBLESHOOTING));
	readonly isCustomTabsFeatureActiveSignal$ = toSignal(inject(FeatureService).isFeatureOn(LDFeatureKey.CUSTOM_TABS));
	readonly isAuditLogsFeatureAvailableSignal$ = computed(() => {
		if (!this.isUserSealightsAdminSignal$()) {
			return this.#permissionsService.userHasPermission('up-management');
		}

		return true;
	});

	readonly displayLiveLabsTabSignal$ = computed(
		() => this.isLiveLabsEnabledSignal$() && !(this.isUserSealightsAdminSignal$() || this.isDevOpsRoleSignal$()),
	);
	readonly isSsoUserSignal$ = inject(AuthStore).isSSOUser;
	readonly originalUserSignal$ = inject(AuthStore).originalUser;
	readonly originalUserDetailsSignal$ = computed(() => {
		if (this.originalUserSignal$()) {
			return [
				`${this.originalUserSignal$()?.name} ${this.originalUserSignal$()?.family_name} (${this.originalUserSignal$()?.email})`,
				`Logged in as ${this.userSignal$()?.name} ${this.userSignal$()?.family_name} (${this.userSignal$()?.email})`,
			];
		}

		return null;
	});
	readonly userTooltipSignal$ = computed(
		() => `${this.userSignal$()?.name} ${this.userSignal$()?.family_name}\n(${this.userSignal$()?.email})`,
	);

	readonly TagSeverity = TagSeverity;
	readonly TagSize = TagSize;
	readonly IconSize = IconSize;

	// Services declaration
	readonly #authService = inject(AuthService);
	readonly #permissionsService = inject(PermissionsService);
	readonly #featureService = inject(FeatureService);
	readonly #userPreferencesService = inject(UserPreferencesService);
	readonly #manualTestService = inject(ManualTestService);
	readonly #headerAnalyticsService = inject(HeaderAnalyticsService);
	readonly #browserExtensionService = inject(BrowserExtensionService);
	readonly #dialog = inject(MatDialog);
	readonly #confirmationDialogService = inject(ConfirmationDialogService);
	readonly #applicationConfig = inject(APP_CONFIG);
	readonly #store = inject(Store);
	readonly #manualTestExtensionSignalIsOn$ = toSignal(
		this.#featureService.isFeatureOn(LDFeatureKey.MANUAL_TEST_EXTENSION),
	);

	showManualTest(): void {
		if (this.#manualTestExtensionSignalIsOn$()) {
			this.#manualTestService.showOverlay = true;
			return;
		}

		void this.doManualTest();
	}

	async doManualTest(): Promise<void> {
		const promotion = `
			Running manual test from SeaLights Chrome extension will allow SeaLights to collect rich test metrics.
			Consequently, SeaLights will provide more accurate list of recommended tests in future builds.
			<br><br>
			Higher accuracy of recommended tests reduces tests and saves time.
		`;
		const promotionButNotInstalled = `
			${promotion}
			<br><br>
			SeaLights Chrome extension appears to be unavailable from your workstation.
		`;
		const promotionButNotChrome = `
			${promotion}
			<br><br>
			<b>Note:</b> SeaLights Chrome extension is only available in Chrome browser.
		`;
		const confirmationDialogData = {
			title: 'Manual Test',
			content: promotion,
			cancelButtonCaption: 'Continue here',
			showCancelButton: true,
		} as ConfirmationDialogData;
		let confirmHandler = (): void => {};
		let isInstalled = false;
		let isStorePageAvailable = false;

		await this.#browserExtensionService.isInstalled().then(async (found: boolean) => {
			isInstalled = found;
			if (!isInstalled) {
				isStorePageAvailable = await this.#browserExtensionService.isStorePageAvailable();
			}
		});

		if (isChromeBrowser()) {
			if (isInstalled) {
				confirmationDialogData.confirmButtonCaption = 'Go with the extension';
				confirmHandler = (): void => {
					this.#browserExtensionService.requestToken();
					setTimeout(() => {
						if (
							!this.#browserExtensionService.tokenData?.token ||
							this.#browserExtensionService.tokenData?.server !== this.#applicationConfig.uri.apiBaseUrl
						) {
							void this.#browserExtensionService.setToken();
						}
					}, 500);
					setTimeout(() => {
						this.#browserExtensionService.startManualTest();
					}, 2000);
				};
			} else if (isStorePageAvailable) {
				confirmationDialogData.confirmButtonCaption = 'Go to extension store page';
				confirmHandler = (): void => {
					this.#browserExtensionService.visitStore();
				};
			} else {
				confirmationDialogData.content = promotionButNotInstalled;
				confirmationDialogData.confirmButtonCaption = confirmationDialogData.cancelButtonCaption;
				confirmationDialogData.showCancelButton = false;
				confirmHandler = (): void => {
					this.#manualTestService.showOverlay = true;
				};
			}
		} else {
			confirmationDialogData.content = promotionButNotChrome;
			confirmationDialogData.confirmButtonCaption = 'Cancel';
		}

		const dialogRef = this.#confirmationDialogService.openDialog({
			closable: false,
			width: '500px',
			// panelClass: 'manual-test-dialog',
			data: confirmationDialogData,
		});

		dialogRef.onClose.subscribe(result => {
			if (result) {
				confirmHandler();
			} else {
				this.#manualTestService.showOverlay = true;
			}
		});
	}

	navigateToLandingPage(): void {
		this.#userPreferencesService.goToDefaultLandingPage().subscribe();
	}

	infoMenuClick(): void {
		this.#headerAnalyticsService.trackOnClickInfoMenu({});
	}

	sealightsApiReferenceClick(): void {
		this.#headerAnalyticsService.trackOnClickSealightsApiReferencePage({});
	}

	whatIsNewClick(): void {
		this.#headerAnalyticsService.trackOnClickWhatIsNewPage({});
	}
	agentReleaseNotesClicked(): void {
		this.#headerAnalyticsService.trackOnClickAgentReleaseNotes({});
	}

	sealightsLearningCenterClick(): void {
		this.#headerAnalyticsService.trackOnClickSealightsLearningCenterPage({});
	}

	sealightsDocumentationClick(): void {
		this.#headerAnalyticsService.trackOnClickSealightsDocumentationPage({});
	}

	tricentisAcademyPortalClick(): void {
		this.#headerAnalyticsService.trackOnClickTricentisAcademyPortal({});
	}

	supportPortalClick(): void {
		this.#headerAnalyticsService.trackOnClickSupportPortalPage({});
	}

	externalLinkClick(name: string): void {
		this.#trackClickExternalLink(name);
	}

	showChangePasswordDialog(): void {
		const dialogConfig: MatDialogConfig = new MatDialogConfig();
		dialogConfig.panelClass = 'sl-change-password-dialog';
		dialogConfig.disableClose = false;

		const dialogRef = this.#dialog.open(ChangePasswordDialogComponent, dialogConfig);

		dialogRef.updatePosition({
			top: '0',
			right: '0',
		});
	}

	privacyPolicyClick(): void {
		this.#headerAnalyticsService.trackOnClickPrivacyPolicyPage({});
	}

	termsOfUseClick(): void {
		this.#headerAnalyticsService.trackOnClickTermsOfUsePage({});
	}

	logoutFromImpersonatedUser(): void {
		this.#store.dispatch(GlobalStoreActions.globalStoreSetLoginAsUser(null));
		setTimeout(() => location.reload());
	}

	logout(): void {
		void this.#authService.logout().pipe(take(1)).subscribe();
	}

	#trackClickExternalLink(name: string): void {
		void this.#headerAnalyticsService.trackClickExternalLink(() => ({
			name,
		}));
	}
}
